<template>
  <section class="ee-login">
    <div class="ee-login__bg" />
    <div class="ee-login__top">
      <div class="ee-login__logo" />
      <h1>{{ title }}</h1>
      <p v-if="dateRange">{{ dateRange }}</p>
    </div>

    <div class="ee-login__bottom">
      <div v-if="info" class="ee-login__note ee-login__note--info">{{ $t('login.logout_another_device') }}</div>
      <div v-if="error && !pinOpen" class="ee-login__note ee-login__note--error" v-html="error" />
      <button type="button" class="ee-btn ee-btn--primary ee-btn--block" @click="start">
        <span class="material-symbols-outlined">lock</span>{{ tm('sign_in_pin', 'Sign in with PIN') }}
      </button>
      <button type="button" class="ee-btn ee-btn--block ee-login__ghost" @click="helpOpen = true">
        <span class="material-symbols-outlined">help</span>{{ tm('no_pin', "I don't have a PIN") }}
      </button>
      <div class="ee-login__hint">{{ twoFactorAuthenticationEnabled ? $t('login.email_enter_info').replace(/<[^>]*>/g, '') : tm('pin_hint', 'Your PIN was sent with your registration confirmation.') }}</div>
    </div>

    <!-- PIN pad -->
    <transition name="ee-pin">
      <div v-if="pinOpen" class="ee-pin">
        <button type="button" class="ee-pin__back" :aria-label="tm('back', 'Back')" @click="closePin">
          <span class="material-symbols-outlined">arrow_back</span>
        </button>
        <div class="ee-pin__head">
          <span class="ee-icon-tile ee-pin__lock"><span class="material-symbols-outlined">{{ twoFactorAuthenticationEnabled ? 'sms' : 'lock' }}</span></span>
          <h2>{{ tm('enter_pin', 'Enter your PIN') }}</h2>
          <p v-if="twoFactorAuthenticationEnabled" v-html="$t('login.sms_pin_info', { phone })" />
          <p v-else>{{ tm('pin_sub', '6-digit code from your registration e-mail') }}</p>
        </div>
        <div class="ee-pin__dots" :class="{ 'is-err': shake, 'is-ok': success, 'is-busy': working && !success }">
          <i v-for="n in pinLength" :key="n" :class="{ 'is-on': n <= pin.length }" :style="{ '--n': n }" />
          <!-- invisible field over the dots: long-press "Paste" on phones and SMS one-time-code autofill -->
          <input
            class="ee-pin__paste-field"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            :aria-label="tm('enter_pin', 'Enter your PIN')"
            :value="pin"
            @input="fillPin($event.target.value); $event.target.value = pin"
          >
        </div>
        <!-- while the server checks the PIN: visible "something is happening" -->
        <div v-if="working && !success" class="ee-pin__status" role="status">
          <span class="ee-spinner" />{{ tm('signing_in', 'Signing in…') }}
        </div>
        <div v-else-if="error" class="ee-pin__msg" v-html="error" />
        <div v-else class="ee-pin__msg">
          <button v-if="canReadClipboard" type="button" class="ee-pin__paste" @click="pasteFromClipboard">
            <span class="material-symbols-outlined">content_paste</span>{{ tm('paste_pin', 'Paste PIN') }}
          </button>
        </div>
        <div class="ee-pin__pad">
          <button v-for="n in 9" :key="n" type="button" :disabled="working" @click="press(String(n))">{{ n }}</button>
          <button type="button" class="ee-pin__fn" @click="helpOpen = true">{{ tm('forgot', 'Forgot?') }}</button>
          <button type="button" :disabled="working" @click="press('0')">0</button>
          <button type="button" class="ee-pin__fn" :aria-label="tm('delete', 'Delete')" @click="press('del')">
            <span class="material-symbols-outlined">backspace</span>
          </button>
        </div>
        <transition name="ee-fade">
          <div v-if="success" class="ee-pin__success">
            <div>
              <div class="ee-check"><span class="material-symbols-outlined">check</span></div>
              <p>{{ welcome }}</p>
            </div>
          </div>
        </transition>
      </div>
    </transition>

    <!-- 2FA: e-mail first, then the SMS PIN -->
    <v-bottom-sheet v-model="emailOpen" max-width="560">
      <div class="ee-sheet">
        <div class="ee-more__grab" />
        <div class="ee-sheet__head">
          <h2 class="ee-sheet__title" style="flex: 1" v-html="$t('login.email_enter_info')" />
        </div>
        <input
          v-model="email"
          class="ee-field"
          type="email"
          autocomplete="email"
          placeholder="name@institution.org"
          @keyup.enter="requestSMS"
        >
        <div v-if="error" class="ee-login__note ee-login__note--error" style="margin-top: 10px" v-html="error" />
        <button type="button" class="ee-btn ee-btn--primary ee-btn--block" style="margin-top: 12px" :disabled="working || !email" @click="requestSMS">
          <span class="material-symbols-outlined">send</span>{{ $t('login.send_login') }}
        </button>
      </div>
    </v-bottom-sheet>

    <!-- help: no PIN -->
    <v-bottom-sheet v-model="helpOpen" max-width="560" @input="v => !v && resetPinMail()">
      <div class="ee-sheet">
        <div class="ee-more__grab" />
        <!-- the PIN can be e-mailed (admin → Settings) -->
        <template v-if="pinByEmail && pinMail === 'form'">
          <div class="ee-sheet__head">
            <div style="flex: 1">
              <h2 class="ee-sheet__title">{{ tm('get_pin_title', 'Get your PIN') }}</h2>
              <p class="ee-muted" style="margin: 6px 0 0">{{ tm('get_pin_text', "We'll e-mail your PIN to the address you registered with.") }}</p>
            </div>
          </div>
          <input
            v-model="pinEmail"
            class="ee-field"
            type="email"
            autocomplete="email"
            placeholder="name@institution.org"
            @keyup.enter="sendPinEmail"
          >
          <div v-if="pinEmailError" class="ee-login__note ee-login__note--error" style="margin-top: 10px">{{ pinEmailError }}</div>
          <button type="button" class="ee-btn ee-btn--primary ee-btn--block" style="margin-top: 12px" :disabled="!pinEmail" @click="sendPinEmail">
            <span class="material-symbols-outlined">send</span>{{ tm('send_pin', 'Send PIN') }}
          </button>
        </template>
        <!-- sending → check / failed; a success moves on to "Check your inbox" -->
        <div v-else-if="pinByEmail && pinMail !== 'sent'" class="ee-mailstate" role="status" aria-live="polite">
          <div v-if="pinMail === 'sending'" class="ee-mailstate__fly"><span class="material-symbols-outlined">mail</span></div>
          <div v-else class="ee-check" :class="{ 'ee-check--fail': pinMail === 'fail' }">
            <span class="material-symbols-outlined">{{ pinMail === 'fail' ? 'close' : 'check' }}</span>
          </div>
          <template v-if="pinMail === 'sending'">
            <h3>{{ tm('sending_pin', 'Sending your PIN…') }}</h3>
            <div class="ee-mailstate__bar"><i /></div>
          </template>
          <h3 v-else-if="pinMail === 'ok'">{{ tm('pin_sent', 'PIN sent') }}</h3>
          <template v-else>
            <h3>{{ tm('pin_failed', "We couldn't send the e-mail") }}</h3>
            <p v-html="pinEmailError" />
            <button type="button" class="ee-btn ee-btn--primary ee-btn--block" @click="pinMail = 'form'">
              <span class="material-symbols-outlined">refresh</span>{{ tm('try_again', 'Try again') }}
            </button>
          </template>
        </div>
        <div v-else-if="pinByEmail" class="ee-empty" style="padding: 18px 10px 6px">
          <span class="ee-empty__ic"><span class="material-symbols-outlined">mark_email_read</span></span>
          <h3>{{ tm('check_inbox', 'Check your inbox') }}</h3>
          <p>{{ tm('pin_sent_text', 'If the address is registered for {title}, your PIN is on its way. Check also the spam folder.', { title }) }}</p>
          <button type="button" class="ee-btn ee-btn--dark ee-btn--block" @click="helpOpen = false">{{ tm('got_it', 'Got it') }}</button>
        </div>
        <div v-else class="ee-empty" style="padding: 18px 10px 6px">
          <span class="ee-empty__ic"><span class="material-symbols-outlined">mail</span></span>
          <h3>{{ tm('no_pin_title', 'Where is my PIN?') }}</h3>
          <p>{{ tm('no_pin_text', 'Your PIN was sent to the e-mail address you registered with. Please check your inbox and spam folder.') }}</p>
          <div v-if="contactInfo.email || contactInfo.phone" class="ee-list" style="margin: 0 0 14px; text-align: left">
            <a v-if="contactInfo.email" class="ee-row" :href="`mailto:${contactInfo.email}`">
              <span class="ee-row__ico"><span class="material-symbols-outlined">mail</span></span>
              <span class="ee-row__txt"><span class="ee-row__t1">{{ contactInfo.email }}</span></span>
            </a>
            <a v-if="contactInfo.phone" class="ee-row" :href="`tel:${contactInfo.phone}`">
              <span class="ee-row__ico"><span class="material-symbols-outlined">call</span></span>
              <span class="ee-row__txt"><span class="ee-row__t1">{{ contactInfo.phone }}</span></span>
            </a>
          </div>
          <p v-else>{{ tm('no_pin_desk', 'You can also ask at the registration desk.') }}</p>
          <button type="button" class="ee-btn ee-btn--dark ee-btn--block" @click="helpOpen = false">{{ tm('got_it', 'Got it') }}</button>
        </div>
      </div>
    </v-bottom-sheet>
  </section>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters, mapMutations, mapState } from 'vuex'
import i18nMixin from '../../modern/i18n'
import { conferenceDays } from '../../modern/sessions'

// PINs are generated as 6 digits (rand(100000, 999999))
const PIN_LENGTH = 6

export default {
  mixins: [i18nMixin],

  data () {
    const logoutReason = sessionStorage.getItem('logout_reason')
    if (logoutReason) sessionStorage.removeItem('logout_reason')

    return {
      pinLength: PIN_LENGTH,
      canReadClipboard: !!(window.isSecureContext && navigator.clipboard && navigator.clipboard.readText),
      pin: '',
      email: '',
      phone: '',
      emailConfirmed: false,
      pinOpen: false,
      emailOpen: false,
      helpOpen: false,
      working: false,
      shake: false,
      success: false,
      welcome: '',
      error: null,
      info: !!logoutReason,
      pinEmail: '',
      pinMail: 'form', // form | sending | ok | fail | sent
      pinEmailError: null,
    }
  },

  computed: {
    ...mapState({
      title: state => state.common.title,
      twoFactorAuthenticationEnabled: state => state.auth.twoFactorAuthenticationEnabled,
      contactInfo: state => state.common.contactInfo,
      pinByEmail: state => state.common.pinByEmail,
      deviceId: state => state.common.deviceId,
      redirectUrl: state => state.auth.redirectUrl,
      blocks: state => state.sessions.sessions,
    }),

    ...mapGetters('common', ['isVirtual']),

    dateRange () {
      const days = conferenceDays(this.blocks)
      if (!days.length) return ''
      const first = dayjs(days[0])
      const last = dayjs(days[days.length - 1])
      return first.isSame(last, 'month') ? `${first.format('D')}–${last.format('D MMMM YYYY')}` : `${first.format('D MMMM')} – ${last.format('D MMMM YYYY')}`
    },
  },

  created () {
    this.$store.commit('setTitle', '')
  },

  mounted () {
    if (EasyEvent.fromAutologin) {
      if (!EasyEvent.autologin_success) {
        this.setError(this.$t('login.autologin_failed'))
      } else if (this.twoFactorAuthenticationEnabled) {
        if (!EasyEvent.two_fa_phone) {
          this.setError(this.$t('login.phone_not_set'))
        } else {
          this.emailConfirmed = true
          this.phone = EasyEvent.two_fa_phone
          this.pinOpen = true
        }
      }
      EasyEvent.fromAutologin = false
    }

    this.onKey = e => {
      // typing into the paste field is handled by its input event
      if (!this.pinOpen || e.target.classList.contains('ee-pin__paste-field')) return
      if (/^\d$/.test(e.key)) this.press(e.key)
      if (e.key === 'Backspace') this.press('del')
    }
    // Ctrl+V / Cmd+V anywhere on the PIN screen
    this.onPaste = e => {
      if (!this.pinOpen || e.target.classList.contains('ee-pin__paste-field')) return
      const text = (e.clipboardData || window.clipboardData).getData('text')
      if (text) {
        e.preventDefault()
        this.fillPin(text)
      }
    }
    document.addEventListener('keydown', this.onKey)
    document.addEventListener('paste', this.onPaste)
  },

  beforeDestroy () {
    document.removeEventListener('keydown', this.onKey)
    document.removeEventListener('paste', this.onPaste)
  },

  methods: {
    ...mapMutations('auth', ['setUser']),

    start () {
      this.error = null
      this.info = false
      if (this.twoFactorAuthenticationEnabled && !this.emailConfirmed) {
        this.emailOpen = true
      } else {
        this.pinOpen = true
      }
    },

    closePin () {
      this.pinOpen = false
      this.pin = ''
      this.error = null
    },

    press (key) {
      if (this.working || this.success || this.shake) return
      this.shake = false
      this.error = null
      if (key === 'del') {
        this.pin = this.pin.slice(0, -1)
      } else if (this.pin.length < PIN_LENGTH) {
        this.pin += key
      }
      if (navigator.vibrate) navigator.vibrate(6)
      if (this.pin.length === PIN_LENGTH) {
        // busy state right away, the request follows once the last dot is drawn
        this.working = true
        setTimeout(this.sendLogin, 150)
      }
    },

    // pasted / autofilled text: digits only ("Your PIN: 123 456" works), submits when complete
    fillPin (text) {
      if (this.working || this.success || this.shake) return
      const digits = String(text).replace(/\D/g, '').slice(0, PIN_LENGTH)
      if (!digits) return
      this.error = null
      this.pin = digits
      if (this.pin.length === PIN_LENGTH) {
        this.working = true
        setTimeout(this.sendLogin, 150)
      }
    },

    // the async clipboard API exists only on HTTPS (and asks for permission on some browsers)
    pasteFromClipboard () {
      navigator.clipboard.readText()
        .then(text => {
          if (/\d/.test(text)) this.fillPin(text)
          else this.setError(this.tm('clipboard_no_pin', 'There is no PIN in the clipboard.'))
        })
        .catch(() => {})
    },

    resetPinMail () {
      clearTimeout(this.pinMailTimer)
      this.pinMail = 'form'
      this.pinEmailError = null
    },

    sendPinEmail () {
      if (!this.pinEmail || this.pinMail === 'sending') return
      this.pinMail = 'sending'
      this.pinEmailError = null
      // the progress stays up for a moment even on a fast reply, so it reads as "something happened"
      const shown = new Promise(resolve => setTimeout(resolve, 900))
      const request = axios.post('/login/pin-email', { email: this.pinEmail.trim() })
      Promise.all([request, shown])
        .then(() => {
          this.pinMail = 'ok'
          this.pinMailTimer = setTimeout(() => { this.pinMail = 'sent' }, 1300)
        })
        .catch(error => {
          const status = error.response && error.response.status
          if (status === 422) {
            // a typo in the address: back to the form with the hint
            this.pinMail = 'form'
            this.pinEmailError = this.tm('invalid_email', 'Please enter a valid e-mail address.')
            return
          }
          this.pinMail = 'fail'
          this.pinEmailError = this.withSupport(status === 429 ? this.$t('login.too_many_wrong_login')
            : this.tm('pin_failed_text', 'Please try again in a moment.'))
        })
    },

    requestSMS () {
      if (!this.email) return
      this.working = true
      this.setError(null)

      axios.post('/login/sms', { email: this.email })
        .then(response => {
          this.phone = response.data.phone
          this.emailConfirmed = true
          this.emailOpen = false
          this.pinOpen = true
        })
        .catch(error => {
          const status = error.response && error.response.status
          this.setError(this.$t(status === 404 ? 'login.email_not_found'
            : status === 422 ? 'login.phone_not_set'
              : status === 429 ? 'login.too_many_wrong_login'
                : 'common.communicationError'))
        })
        .finally(() => {
          this.working = false
        })
    },

    sendLogin () {
      this.working = true
      const subscription = JSON.parse(localStorage.getItem('subscription'))

      axios.post('/login', { pin: this.pin, device_id: this.deviceId, endpoint: subscription ? subscription.endpoint : null })
        .then(response => {
          const visitor = response.data.visitor
          this.setUser({ user: visitor, fromLogin: true })
          this.welcome = this.tm('welcome_name', 'Welcome, {name}!', { name: visitor.firstname || '' })
          this.success = true

          // after the success animation reload into the app. Only the hash changes, so the
          // reload can't race a navigation (assigning '/#' to a URL with a query string
          // started a new page load that reload() cancelled -> back on #/login).
          setTimeout(() => {
            const target = !this.isVirtual && this.redirectUrl && this.redirectUrl !== '/login' ? this.redirectUrl : '/'
            window.location.hash = target
            window.location.reload()
          }, 1100)
        })
        .catch(error => {
          const status = error.response && error.response.status
          this.setError(this.$t(status === 422 ? 'login.wrong_pin' : status === 429 ? 'login.too_many_wrong_login' : 'common.communicationError'))
          this.shake = true
          if (navigator.vibrate) navigator.vibrate(60)
          this.working = false
          // keypad stays locked until the shake has played
          setTimeout(() => {
            this.pin = ''
            this.shake = false
          }, 500)
        })
    },

    setError (error) {
      this.error = error ? this.withSupport(error) : null
    },

    // message + the event's contact (admin → Settings)
    withSupport (message) {
      const { email, phone } = this.contactInfo
      if (email && phone) return `${message}<br>${this.$t('login.support.contact_both', { email, phone })}`
      if (email) return `${message}<br>${this.$t('login.support.contact_email', { email })}`
      if (phone) return `${message}<br>${this.$t('login.support.contact_phone', { phone })}`
      return message
    },
  },
}
</script>
